import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLoading } from '../../contexts/LoadingContext';
import { useGetUser } from '../../hooks/db/useGetUser';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useUpdateUser } from '../../hooks/db/useUpdateUser';

const EditUser: React.FC = () => {
    const { getUser } = useGetUser()
    const { updateUser } = useUpdateUser()
    const navigate = useNavigate()
    const[isSubmitting, setIsSubmitting] = useState(false)
    const { setLoading } = useLoading()

    const { id } = useParams()
    const [formData, setFormData] = useState({
        name: '',
        email: '',
    
        role: '',
        companyName: '',
        companyTitle: '',
        phone: '',
        quotaHourly: 0,
        quotaDaily: 0,
        quotaMonthly: 0,
        quotaFixed: 0,
    })


    useEffect(() => {
        const fetchUser = async () => {
            if (id) {
                setLoading(true);
                const result = await getUser(id)
                if (result && result.success) {
                    const user = result.user
                    setFormData({
                        name: user.name || '',
                        email: user.email || '',
                        role: user.role || '',
                        companyName: user.companyName || '',
                        companyTitle: user.companyTitle || '',
                        phone: user.phone || '',
                        quotaHourly: user.quotas? user.quotas?.find((q: { method: string; }) => q.method === 'hourly')?.count : 0,
                        quotaDaily: user.quotas? user.quotas?.find((q: { method: string; }) => q.method === 'daily')?.count: 0,
                        quotaMonthly: user.quotas? user.quotas?.find((q: { method: string; }) => q.method === 'monthly')?.count : 0,
                        quotaFixed: user.quotas? user.quotas?.find((q: { method: string; }) => q.method === 'fixed')?.count : 0,
                    })
                }else{
                    toast.error("Something went wrong, please refresh the page!")
                }
                setLoading(false)
            }
        };

        fetchUser()
    }, [id])

   
    const [errors, setErrors] = useState({
        name: '',
        email: '',
        password: '',
        role: '',
        companyName: '',
        companyTitle: '',
        phone: '',
    })

    const validateForm = () => {
        const newErrors: typeof errors = {
            name: '',
            email: '',
            password: '',
            role: '',
            companyName: '',
            companyTitle: '',
            phone: '',
        }

        if (!formData.name) newErrors.name = 'Name is required.'
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email))
            newErrors.email = 'Valid email is required.'
        if (!formData.role) newErrors.role = 'Role is required.'
        if (!formData.companyName) newErrors.companyName = 'Company name is required.'
        if (!formData.companyTitle) newErrors.companyTitle = 'Company title is required.'
        if (!formData.phone)
            newErrors.phone = 'Phone number is required.'

        setErrors(newErrors)

        return Object.values(newErrors).every((error) => error === '')
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (validateForm()) {
            setIsSubmitting(true)
            setLoading(true)
            const { quotaHourly, quotaDaily, quotaMonthly, quotaFixed, ...userDetails } = formData;
            const payload = {
                ...userDetails,
                quotas: [
                    { method: 'hourly', count: quotaHourly },
                    { method: 'daily', count: quotaDaily },
                    { method: 'monthly', count: quotaMonthly },
                    { method: 'fixed', count: quotaFixed },
                ],
                _id: id
            };

            if (id) {
                const result = await updateUser(id, payload)

                if (result && 'success' in result && result.success && 'userId' in result && result.userId) {
                    toast.success('User updated successfully!')
                    setIsSubmitting(false)
                    setLoading(false)
                    navigate(`/admin/users`)
                } else {
                    toast.error(result?.message || 'Failed to update the user. Please try again.')
                    setIsSubmitting(false)
                    setLoading(false)
                }
            } else {
                toast.error('User ID is missing. Please try again.')
                setIsSubmitting(false)
                setLoading(false)
            }
                        
        }
    }

    function handleCancel (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e.preventDefault()
        navigate(`/admin/users`)
    }

    return (
        <div className=" mx-auto p-6 bg-white shadow-md rounded-md">
            <h1 className="text-2xl font-bold mb-6 text-center">Edit User</h1>
            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4">
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">Name:</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.name && (
                            <span className="text-red-500 text-sm">{errors.name}</span>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">Email:</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.email && (
                            <span className="text-red-500 text-sm">{errors.email}</span>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">Role:</label>
                        <select
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select Role</option>
                            <option value="ADMIN">Admin</option>
                            <option value="USER">User</option>
                        </select>
                        {errors.role && (
                            <span className="text-red-500 text-sm">{errors.role}</span>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">Company Name:</label>
                        <input
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.companyName && (
                            <span className="text-red-500 text-sm">{errors.companyName}</span>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">Company Title:</label>
                        <input
                            type="text"
                            name="companyTitle"
                            value={formData.companyTitle}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.companyTitle && (
                            <span className="text-red-500 text-sm">{errors.companyTitle}</span>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">Phone:</label>
                        <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.phone && (
                            <span className="text-red-500 text-sm">{errors.phone}</span>
                        )}
                    </div>
                    <div className="mb-4 col-span-2">
                        <label className="block text-sm font-medium mb-1">Quota:</label>
                        <div className="grid grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Hourly:</label>
                                <input
                                    type="number"
                                    name="quotaHourly"
                                    value={formData.quotaHourly}
                                    onChange={handleChange}
                                    min={0}
                                    required
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Daily:</label>
                                <input
                                    type="number"
                                    name="quotaDaily"
                                    value={formData.quotaDaily }
                                    onChange={handleChange}
                                    min={0}
                                    required
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Monthly:</label>
                                <input
                                    type="number"
                                    name="quotaMonthly"
                                    value={formData.quotaMonthly}
                                    onChange={handleChange}
                                    min={0}
                                    required
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Fixed:</label>
                                <input
                                    type="number"
                                    name="quotaFixed"
                                    value={formData.quotaFixed }
                                    onChange={handleChange}
                                    min={0}
                                    required
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="p-4 bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 transition"
                >
                   Update
                </button>

                <button
                    onClick={(e) => {handleCancel(e)}}
                    className="ml-4 p-4 bg-red-500 text-white py-2 rounded-md hover:bg-blue-600 transition"
                >
                    Cancel
                </button>
            </form>
        </div>
    )
}

export default EditUser
